import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { closeDialog, displayDialog, displaySuccessMessage } from "store/actions/utilActions";
//#endregion
//#region types
import { CustomerFormTypes } from "models/formTypes";
import { DialogEnums, StyleEnums } from "models/enums";
import { ResponseError } from "models/utilTypes/apiClientTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { useCustomerService } from "serviceHooks";
import ImportProblemList from "./importProblemList";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

export const InvalidOrgNrsErrorCode = "InvalidOrgNrsInImport";
export const InvalidOrgNrCustomerErrorCode = "InvalidOrgNrCustomerInImport";
export const MissingCityErrorCode = "MissingCityInImport";
export const MissingCityCustomerErrorCode = "MissingCityCustomerInImport";
const ConfirmableErrorCodes = [
  InvalidOrgNrsErrorCode,
  InvalidOrgNrCustomerErrorCode,
  MissingCityErrorCode,
  MissingCityCustomerErrorCode,
];

interface ImportChoices {
  importWithoutInvalidOrgNr: boolean;
  skipCustomersWithInvalidOrgNr: boolean;
  importWithoutCity: boolean;
  skipCustomersWithoutCity: boolean;
}

const NoChoices: ImportChoices = {
  importWithoutInvalidOrgNr: false,
  skipCustomersWithInvalidOrgNr: false,
  importWithoutCity: false,
  skipCustomersWithoutCity: false,
};

interface ConfirmStep {
  dialogId: string;
  title: string;
  description: string;
  customerErrorCode: string;
  importWithout: string;
  importWithoutChoice: Partial<ImportChoices>;
  skipChoice: Partial<ImportChoices>;
}

const ConfirmSteps: Record<string, ConfirmStep> = {
  [InvalidOrgNrsErrorCode]: {
    dialogId: "customerImportInvalidOrgNr",
    title: TranslationKeys.Customer.ImportInvalidOrgNrTitle,
    description: TranslationKeys.Customer.ImportInvalidOrgNrDescription,
    customerErrorCode: InvalidOrgNrCustomerErrorCode,
    importWithout: TranslationKeys.Customer.ImportWithoutOrgNr,
    importWithoutChoice: { importWithoutInvalidOrgNr: true },
    skipChoice: { skipCustomersWithInvalidOrgNr: true },
  },
  [MissingCityErrorCode]: {
    dialogId: "customerImportMissingCity",
    title: TranslationKeys.Customer.ImportMissingCityTitle,
    description: TranslationKeys.Customer.ImportMissingCityDescription,
    customerErrorCode: MissingCityCustomerErrorCode,
    importWithout: TranslationKeys.Customer.ImportWithoutCity,
    importWithoutChoice: { importWithoutCity: true },
    skipChoice: { skipCustomersWithoutCity: true },
  },
};

type ImportOutcome = { done: true } | { done: false; error: unknown };

/** Submits a customer import, asking one question at a time about customers without a valid org nr or a city. */
function useCustomerImportSubmit(onFormSubmitSuccess?: () => void) {
  const customerService = useCustomerService();
  const dispatch = useDispatch();

  const importCustomers = (values: CustomerFormTypes.CustomerImport, choices: ImportChoices) => {
    const uploadedFile = values.csvUpload ? values.csvUpload[0] : undefined;
    const file = uploadedFile
      ? {
          fileName: uploadedFile.fileName,
          contentType: uploadedFile.contentType,
          data: uploadedFile.data,
        }
      : undefined;
    return customerService.importCustomerRegister(
      {
        csvUpload: file,
        ...choices,
        customerNameColumn: values.customerNameColumn,
        orgNrColumn: values.orgNrColumn,
        emailAddressColumn: values.emailAddressColumn,
        phoneNumberColumn: values.phoneNumberColumn,
        billingStreetColumn: values.billingStreetColumn,
        billingStreet2Column: values.billingStreet2Column,
        billingZipCodeColumn: values.billingZipCodeColumn,
        billingCityColumn: values.billingCityColumn,
        billingCountryCodeColumn: values.billingCountryCodeColumn,
        deliveryStreetColumn: values.deliveryStreetColumn,
        deliveryStreet2Column: values.deliveryStreet2Column,
        deliveryZipCodeColumn: values.deliveryZipCodeColumn,
        deliveryCityColumn: values.deliveryCityColumn,
        deliveryCountryCodeColumn: values.deliveryCountryCodeColumn,
        vatNumberColumn: values.vatNumberColumn,
        glnNumberColumn: values.glnNumberColumn,
        includeVatNumberOnInvoiceColumn: values.includeVatNumberOnInvoiceColumn,
        customerStandardPaymentTermsColumn: values.customerStandardPaymentTermsColumn,
        showCurrencyCodeColumn: values.showCurrencyCodeColumn,
        languageColumn: values.languageColumn,
        referencePersonImportColumnMappings: values.referencePersons?.map((item) => ({
          referenceNameColumn: item.nameColumn,
          referenceMailColumn: item.mailColumn,
          referencePhoneColumn: item.phoneColumn,
          referenceIdentifierColumn: item.identifierColumn,
          referenceIsDefaultColumn: item.isDefaultColumn,
        })),
        propertyDesignationColumn: values.propertyDesignationColumn,
        tenantsOwnerAssociationOrgNumberColumn: values.tenantsOwnerAssociationOrgNumberColumn,
        apartmentNumberColumn: values.apartmentNumberColumn,
        delimiter: WintSuperkollAPIModel.CsvImportDelimiter.Comma,
      },
      { hideErrorsForCodes: ConfirmableErrorCodes }
    );
  };

  const getConfirmStep = (error: any): ConfirmStep | undefined => {
    const codes: string[] = error?.codes ?? [];
    if (!codes.length || !codes.every((code) => ConfirmableErrorCodes.includes(code))) {
      return undefined;
    }
    return ConfirmSteps[codes.find((code) => code in ConfirmSteps) ?? ""];
  };

  // Resolves without importing when it has opened the next question, so the dialog that asked the previous one can close.
  const attemptImport = (values: CustomerFormTypes.CustomerImport, choices: ImportChoices): Promise<ImportOutcome> =>
    importCustomers(values, choices).then(
      () => ({ done: true }),
      (error) => {
        const step = getConfirmStep(error);
        if (!step) {
          throw error;
        }
        const customers = ((error?.errors ?? []) as ResponseError[])
          .filter((e) => e.code === step.customerErrorCode)
          .map((e) => e.description);
        // A server that sends the customers inside one sentence still has to show them rather than an empty list.
        showConfirmDialog(step, customers.length ? customers : [error?.message].filter(Boolean), values, choices);
        return { done: false, error };
      }
    );

  const continueImport = (values: CustomerFormTypes.CustomerImport, choices: ImportChoices, dialogId: string) =>
    attemptImport(values, choices).then((outcome) => {
      if (outcome.done) {
        dispatch(closeDialog(dialogId));
        dispatch(
          displaySuccessMessage(
            LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.CustomerImportSuccess)
          )
        );
        onFormSubmitSuccess && onFormSubmitSuccess();
      }
    });

  const showConfirmDialog = (
    step: ConfirmStep,
    customers: string[],
    values: CustomerFormTypes.CustomerImport,
    choices: ImportChoices
  ) => {
    dispatch(
      displayDialog({
        id: step.dialogId,
        isOpen: false,
        title: LocalizationHelperInstance.getFormattedMessage(step.title),
        titleIcon: IconDefinitionConstants.WInfo,
        actions: [
          {
            text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Customer.ImportSkipTheseCustomers),
            isSecondary: true,
            onClick: () => continueImport(values, { ...choices, ...step.skipChoice }, step.dialogId),
          },
          {
            text: LocalizationHelperInstance.getFormattedMessage(step.importWithout),
            type: StyleEnums.DisplayType.Success,
            onClick: () => continueImport(values, { ...choices, ...step.importWithoutChoice }, step.dialogId),
          },
        ],
        dialogType: DialogEnums.DialogType.Message,
        type: StyleEnums.DisplayType.Info,
        // Three footer buttons wrap onto two rows at the default sm width.
        maxWidth: "md",
        children: (
          <ImportProblemList
            description={LocalizationHelperInstance.getFormattedMessage(step.description)}
            customers={customers}
          />
        ),
      })
    );
  };

  const handleSubmit = (values: CustomerFormTypes.CustomerImport) =>
    attemptImport(values, NoChoices).then((outcome) => {
      if (!outcome.done) {
        throw outcome.error;
      }
    });

  return handleSubmit;
}

export default useCustomerImportSubmit;
